import React, { useState, useEffect, useCallback } from 'react';
import { 
  TextField, 
  Typography, 
  Grid, 
  Paper,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { WalletConfig, LogLevel, CreateLogParams } from '../../../types/dex';
import { useDexService } from '../../../contexts/DexServiceContext';
import { ethers } from 'ethers';

interface WalletConfigSectionProps {
  wallets: WalletConfig[];
  onChange: (wallets: WalletConfig[]) => void;
}

/**
 * 钱包配置区域组件
 * 用于管理多个钱包的私钥和地址
 */
const WalletConfigSection: React.FC<WalletConfigSectionProps> = ({ wallets, onChange }) => {
  const { addLog } = useDexService();
  const [localWallets, setLocalWallets] = useState<WalletConfig[]>(wallets);
  const [newPrivateKey, setNewPrivateKey] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [error, setError] = useState('');
  const [derivedAddress, setDerivedAddress] = useState('');
  
  // 删除确认对话框状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState<number | null>(null);
  
  // 复制成功消息状态
  const [copySuccess, setCopySuccess] = useState('');

  // 同步外部钱包变更
  useEffect(() => {
    setLocalWallets(wallets);

    console.log('WalletConfigSection wallets:', wallets);

    if (wallets.length > 0) {
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: '已加载钱包配置',
        data: {
          count: wallets.length,
          addresses: wallets.filter(w => w.address).map(w => formatAddressForDisplay(w.address))
        }
      };
      addLog(log);
    }
  }, [wallets, addLog]);

  // 验证私钥并派生地址
  const validatePrivateKey = useCallback((privateKey: string) => {
    if (!privateKey) {
      setDerivedAddress('');
      return false;
    }
    
    try {
      const formattedKey = privateKey.startsWith('0x') ? privateKey : `0x${privateKey}`;
      const wallet = new ethers.Wallet(formattedKey);
      const address = wallet.address;
      setDerivedAddress(address);
      setError('');
      
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: '私钥验证成功，已派生钱包地址',
        data: {
          address: formatAddressForDisplay(address)
        }
      };
      addLog(log);
      
      return true;
    } catch (error) {
      setDerivedAddress('');
      setError('私钥格式无效');
      
      const log: CreateLogParams = {
        level: LogLevel.ERROR,
        message: '私钥验证失败',
        data: {
          error: error instanceof Error ? error.message : '未知错误'
        }
      };
      addLog(log);
      
      return false;
    }
  }, [addLog]);

  // 处理私钥输入变更
  const handlePrivateKeyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPrivateKey(value);
    validatePrivateKey(value);
  }, [validatePrivateKey]);

  // 添加新钱包
  const handleAddWallet = useCallback(() => {
    if (validatePrivateKey(newPrivateKey)) {
      // 检查钱包是否已存在
      const formattedKey = newPrivateKey.startsWith('0x') ? newPrivateKey : `0x${newPrivateKey}`;
      const exists = localWallets.some(wallet => 
        wallet.privateKey === formattedKey || 
        wallet.privateKey === newPrivateKey
      );
      
      if (exists) {
        setError('此钱包已添加');
        
        const log: CreateLogParams = {
          level: LogLevel.WARNING,
          message: '尝试添加重复的钱包',
          data: {
            address: formatAddressForDisplay(derivedAddress)
          }
        };
        addLog(log);
        return;
      }
      
      // 创建新钱包配置
      const newWallet: WalletConfig = {
        privateKey: formattedKey,
        address: derivedAddress
      };
      
      const updatedWallets = [...localWallets, newWallet];
      setLocalWallets(updatedWallets);
      onChange(updatedWallets);
      
      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: '已添加新钱包',
        data: {
          address: formatAddressForDisplay(derivedAddress),
          totalWallets: updatedWallets.length
        }
      };
      addLog(log);
      
      // 清空输入
      setNewPrivateKey('');
      setDerivedAddress('');
    }
  }, [newPrivateKey, validatePrivateKey, localWallets, onChange, addLog]);

  // 删除钱包
  const handleDeleteWalletConfirm = useCallback((index: number) => {
    const wallet = localWallets[index];
    setWalletToDelete(index);
    setDeleteDialogOpen(true);
    
    const log: CreateLogParams = {
      level: LogLevel.WARNING,
      message: '准备删除钱包',
      data: {
        address: formatAddressForDisplay(wallet.address),
        index
      }
    };
    addLog(log);
  }, [localWallets, addLog]);

  const handleDeleteWallet = useCallback(() => {
    if (walletToDelete !== null) {
      const deletedWallet = localWallets[walletToDelete];
      const updatedWallets = [...localWallets];
      updatedWallets.splice(walletToDelete, 1);
      setLocalWallets(updatedWallets);
      onChange(updatedWallets);
      
      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: '已删除钱包',
        data: {
          address: formatAddressForDisplay(deletedWallet.address),
          remainingWallets: updatedWallets.length
        }
      };
      addLog(log);
      
      setDeleteDialogOpen(false);
      setWalletToDelete(null);
    }
  }, [walletToDelete, localWallets, onChange, addLog]);

  // 复制地址到剪贴板
  const handleCopyAddress = useCallback((address: string) => {
    navigator.clipboard.writeText(address).then(
      () => {
        setCopySuccess('地址已复制到剪贴板');
        setTimeout(() => setCopySuccess(''), 2000);
        
        const log: CreateLogParams = {
          level: LogLevel.INFO,
          message: '已复制钱包地址',
          data: {
            address: formatAddressForDisplay(address)
          }
        };
        addLog(log);
      },
      () => {
        setCopySuccess('复制失败');
        setTimeout(() => setCopySuccess(''), 2000);
        
        const log: CreateLogParams = {
          level: LogLevel.ERROR,
          message: '复制钱包地址失败'
        };
        addLog(log);
      }
    );
  }, [addLog]);

  // 格式化地址显示（保护隐私）
  const formatAddressForDisplay = useCallback((address: string | undefined) => {
    if (!address) return '未知地址';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  }, []);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        钱包管理
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="钱包私钥"
            value={newPrivateKey}
            onChange={handlePrivateKeyChange}
            variant="outlined"
            error={!!error}
            helperText={error || "输入钱包私钥，将自动派生地址"}
            type={showPrivateKey ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPrivateKey(!showPrivateKey)}
                    edge="end"
                  >
                    {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {derivedAddress && (
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                派生地址: {formatAddressForDisplay(derivedAddress)}
              </Typography>
              <Tooltip title="复制完整地址">
                <IconButton 
                  size="small"
                  onClick={() => handleCopyAddress(derivedAddress)}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {copySuccess && (
                <Chip 
                  label={copySuccess} 
                  color="success" 
                  variant="outlined" 
                  size="small"
                />
              )}
            </Stack>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddWallet}
            disabled={!derivedAddress}
          >
            添加钱包
          </Button>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            已添加的钱包 ({localWallets.length})
          </Typography>
          {localWallets.length > 0 ? (
            <List>
              {localWallets.map((wallet, index) => (
                <ListItem 
                  key={index}
                  divider
                  sx={{ backgroundColor: index % 2 === 0 ? 'background.default' : 'inherit' }}
                >
                  <ListItemText
                    primary={`钱包 #${index + 1}: ${formatAddressForDisplay(wallet.address || '')}`}
                    secondary={`私钥: ${wallet.privateKey ? '******************************' : '未设置'}`}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="复制地址">
                      <IconButton 
                        edge="end" 
                        aria-label="copy"
                        onClick={() => handleCopyAddress(wallet.address || '')}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="删除钱包">
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => handleDeleteWalletConfirm(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              暂无钱包，请添加钱包配置
            </Typography>
          )}
        </Grid>
      </Grid>
      
      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除这个钱包配置吗？此操作无法撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteWallet} color="error">删除</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WalletConfigSection;
